import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { Button, DatePicker, FormSelect, Input, NumericField, SearchableSelect } from '@aimsos/ui';

import {
  OPERATORS_WITH_RANGE,
  OPERATORS_WITHOUT_VALUE,
  operatorLabelKey,
  operatorsForType,
} from '../constants/filterOperators.js';

import { X } from 'lucide-react';

/**
 * One filter row: column, operator, value (CT-5193).
 *
 * The operator list and the value control are both driven by the column's
 * `type` **from the service** — never inferred from the display name, which is
 * how `Close Date` ends up with text operators.
 *
 * `memo()` because rows re-render together otherwise; the parent passes one
 * stable handler per intent and the row supplies its own id.
 */
const FilterConfigRow = memo(function FilterConfigRow({
  filter,
  columns,
  columnsByFieldUuid,
  columnsLoading,
  onChange,
  onRemove,
}) {
  const { t } = useTranslation('dashboards');

  const columnType = columnsByFieldUuid[filter.column]?.type ?? null;

  // `fieldUuid`, not `internalName`: `filter.column` is what
  // `buildWidgetPayload` sends straight through as `fieldUuid`.
  const columnOptions = useMemo(
    () => columns.map((column) => ({ id: column.fieldUuid, name: column.displayName })),
    [columns],
  );

  const operatorOptions = useMemo(
    () =>
      operatorsForType(columnType).map((operator) => ({
        value: operator,
        label: t(operatorLabelKey(operator)),
      })),
    [columnType, t],
  );

  const handleColumnSelect = useCallback(
    (option) => {
      // Pick the type's first operator whenever the current one does not belong
      // to it. That covers the fresh row — the prototype's row arrives with `is`
      // already chosen — and the switch from a text column to a numeric one,
      // where `contains` would otherwise stay selected but off the list.
      const nextOperators = operatorsForType(columnsByFieldUuid[option.id]?.type ?? null);
      const patch = { column: option.id };
      if (!nextOperators.includes(filter.operator)) {
        patch.operator = nextOperators[0];
      }
      onChange(filter.id, patch);
    },
    [columnsByFieldUuid, filter.id, filter.operator, onChange],
  );

  const handleOperatorChange = useCallback(
    (next) => onChange(filter.id, { operator: next }),
    [filter.id, onChange],
  );

  const handleTextValue = useCallback(
    (event) => onChange(filter.id, { value: event.target.value }),
    [filter.id, onChange],
  );

  const handleNumberValue = useCallback(
    (next) => onChange(filter.id, { value: next }),
    [filter.id, onChange],
  );

  const handleNumberValueTo = useCallback(
    (next) => onChange(filter.id, { valueTo: next }),
    [filter.id, onChange],
  );

  const handleDateValue = useCallback(
    (next) => onChange(filter.id, { value: next }),
    [filter.id, onChange],
  );

  const handleDateValueTo = useCallback(
    (next) => onChange(filter.id, { valueTo: next }),
    [filter.id, onChange],
  );

  const handleRemove = useCallback(() => onRemove(filter.id), [filter.id, onRemove]);

  const columnLabel = columnsByFieldUuid[filter.column]?.displayName ?? '';
  // Not `filter.operator && …`: a row with no operator yet still shows the
  // value field. The prototype's row is complete from the moment it appears,
  // and only the two "empty" operators take the field away — leaving it out
  // while the row was blank is what let the operator select swallow the row.
  const takesValue = !OPERATORS_WITHOUT_VALUE.includes(filter.operator);
  const takesRange = OPERATORS_WITH_RANGE.includes(filter.operator);

  const valueControl = () => {
    if (!takesValue) return null;

    if (columnType === 'number') {
      return (
        <div className="flex min-w-[140px] flex-1 items-center gap-2">
          <NumericField
            value={filter.value === '' ? null : Number(filter.value)}
            onValueChange={handleNumberValue}
            placeholder={t('widgetBuilder.filters.valuePlaceholder')}
            aria-label={t('widgetBuilder.filters.valueAria', { column: columnLabel })}
          />
          {takesRange ? (
            // No range control exists in the DS, so `between` is two fields side
            // by side rather than an invented component (CT-5193 scope note).
            <NumericField
              value={filter.valueTo === '' ? null : Number(filter.valueTo)}
              onValueChange={handleNumberValueTo}
              placeholder={t('widgetBuilder.filters.valueToPlaceholder')}
              aria-label={t('widgetBuilder.filters.valueToAria', { column: columnLabel })}
            />
          ) : null}
        </div>
      );
    }

    if (columnType === 'date') {
      return (
        <div className="flex min-w-[140px] flex-1 items-center gap-2">
          {/* `DatePicker` takes no `aria-label`, so the label wraps it. */}
          <label className="min-w-0 flex-1">
            <span className="sr-only">
              {t('widgetBuilder.filters.valueAria', { column: columnLabel })}
            </span>
            <DatePicker value={filter.value} onChange={handleDateValue} />
          </label>
          {takesRange ? (
            <label className="min-w-0 flex-1">
              <span className="sr-only">
                {t('widgetBuilder.filters.valueToAria', { column: columnLabel })}
              </span>
              <DatePicker value={filter.valueTo} onChange={handleDateValueTo} />
            </label>
          ) : null}
        </div>
      );
    }

    return (
      <div className="min-w-[140px] flex-1">
        <Input
          value={filter.value ?? ''}
          onChange={handleTextValue}
          placeholder={t('widgetBuilder.filters.valuePlaceholder')}
          aria-label={t('widgetBuilder.filters.valueAria', { column: columnLabel })}
        />
      </div>
    );
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <div className="min-w-[140px] flex-1">
        <SearchableSelect
          options={columnOptions}
          value={filter.column || null}
          onSelect={handleColumnSelect}
          showAvatar={false}
          isLoading={columnsLoading}
          placeholder={t('widgetBuilder.filters.columnPlaceholder')}
          searchPlaceholder={t('widgetBuilder.filters.columnSearchPlaceholder')}
          ariaLabel={t('widgetBuilder.filters.columnAria')}
        />
      </div>

      {/* Fixed, not flex: the operator holds one short phrase, and letting it
          grow is what made it the widest thing in the row. */}
      <div className="w-[150px] shrink-0">
        <FormSelect
          value={filter.operator}
          onChange={handleOperatorChange}
          options={operatorOptions}
          placeholder={t('widgetBuilder.filters.operatorPlaceholder')}
          // Nothing to offer until a column names a type: an operator list that
          // ignores the type is the bug the matrix exists to prevent.
          disabled={!columnType}
        />
      </div>

      {valueControl()}

      <Button
        variant="ghost"
        size="sm"
        leftIcon={X}
        onClick={handleRemove}
        aria-label={
          columnLabel
            ? t('widgetBuilder.filters.removeNamed', { column: columnLabel })
            : t('widgetBuilder.filters.remove')
        }
      />
    </div>
  );
});

FilterConfigRow.propTypes = {
  filter: PropTypes.shape({
    id: PropTypes.string.isRequired,
    column: PropTypes.string,
    operator: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valueTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnsByFieldUuid: PropTypes.objectOf(PropTypes.object).isRequired,
  columnsLoading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default FilterConfigRow;
